import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useSessionId } from '../../hooks/useSessionId'
import { useUser } from '../../hooks/useUser'
import { useAppSelector } from '../../store'
import {
  useTeam,
  useTeamWherebyMeeting,
  useActivityWherebyMeeting,
  useCreateWherebyMeeting,
  useEnsureActivityWherebyMeeting
} from '../../apiLayer'
import { Team } from '../../../../pure-js/types/Team'
import { WherebyMeeting } from '../../../../pure-js/types/WherebyTypes'
import { resolveWherebyScope } from '../../../../pure-js/libs/resolveWherebyScope'
import { WHEREBY_GAME_ACTIVITY_TYPES } from '../../../../pure-js/libs/wherebyCallOwnership'
import { getWherebyCallDisplayName } from '../../../../pure-js/libs/isWherebyBotParticipant'
import { WherebyCallKind } from '../../../../pure-js/enums/Whereby'
import { ActivityType } from '../../../../pure-js/enums/ActivityType'
import type { SprintActivityWherebyMeetingRequest } from '../../../../pure-js/types/WherebyTypes'
import { GamerRoutes } from '../../config/routes/routeEnums'
import { isGamePagePath } from '../../config/routes/routeConfig'

// Games and the AI coach (SHORT_REFLECTION) have rooms of their own pages; the sprint call has a page too; the rest use the team call.
const AI_COACH_ROUTE_TYPES = new Set<string>([ActivityType.SHORT_REFLECTION])

interface WherebyRoomInfo {
  team?: Team
  meeting?: WherebyMeeting
  roomUrl?: string
  teamName?: string
  displayName: string
}

export interface UseWherebyRoomResult {
  meetingInfo: WherebyRoomInfo | null
  isLoading: boolean
  isFetching: boolean
  error?: unknown
  // Create-or-reuse the room for the current scope (per-activity when in a sprint activity, else team).
  ensureRoom: () => void
  // Refetch the active room query for the current scope.
  refetch: () => void
  isEnsuring: boolean
  // True when the page you're on can't host the currently-resolved activity's call (e.g. you're on the
  // game page but the schedule has advanced to the AI coach). Callers should show no call in this case.
  roomUnavailableForRoute: boolean
  // True when there's a team to host a call (an activity team, or an override). When false there is no
  // call to show/prepare.
  hasScope: boolean
  // True when the resolved scope is a GAME activity's dedicated room. Game calls are desktop-only —
  // entry surfaces use this to block joining from phones/tablets (team/AI-coach calls stay allowed).
  isGameScope: boolean
  // The live activity is a call of the whole sprint, joined on its own page: the way there.
  sprintCall?: SprintActivityWherebyMeetingRequest
  // The resolved team id for the current scope (pin → live → override). Available WITHOUT the team
  // document having loaded — callers must drive create/connect off this, not the (fragile, game-page
  // starved) team-doc read.
  teamId?: string
}

export const useWherebyRoom = (params?: { teamId?: string }): UseWherebyRoomResult => {
  const overrideTeamId = params?.teamId
  const location = useLocation()
  // The gamer session fetch (getUpcomingActivityForGamer) is not valid for facilitators.
  const isFacilitatorContext = location.pathname.startsWith('/facilitator')
  const { data: user, isLoading: isUserLoading } = useUser()
  const {
    teamId,
    sprintId,
    activityId,
    activityType,
    isLoading: isSessionLoading
  } = useSessionId({ skip: Boolean(overrideTeamId) || isFacilitatorContext })

  const isGamePage = isGamePagePath(location.pathname)
  const isAiCoachPage = location.pathname.includes(GamerRoutes.AI_COACH)

  // On the game / AI coach page, use the activity pinned at lobby entry (survives refresh, doesn't drift
  // with the schedule) instead of the live "next activity".
  const callScope = useAppSelector((s) => s.whereby.callScope)
  const pinnedScope =
    !overrideTeamId &&
    ((isGamePage && callScope?.kind === WherebyCallKind.GAME) ||
      (isAiCoachPage && callScope?.kind === WherebyCallKind.AI_COACH))
      ? callScope
      : null

  const effSprintId = pinnedScope?.sprintId ?? sprintId
  const effActivityId = pinnedScope?.activityId ?? activityId
  const effActivityType = pinnedScope?.activityType ?? activityType
  // Team that owns the activity (override → pin → live). Never a membership fallback (that diverges
  // between users and shows a call when nothing is upcoming).
  const activityTeamId = overrideTeamId ?? pinnedScope?.teamId ?? teamId

  // Route gate: a game page may only host a game's room; the AI coach page only the AI coach's. Stops the
  // call drifting into the next activity's room. Skipped on load (no type yet) and for overrides.
  const roomUnavailableForRoute =
    !overrideTeamId &&
    Boolean(effActivityType) &&
    ((isGamePage && !WHEREBY_GAME_ACTIVITY_TYPES.has(effActivityType as string)) ||
      (isAiCoachPage && !AI_COACH_ROUTE_TYPES.has(effActivityType as string)))

  // Activity-vs-team scope decision — shared with DashboardCall via resolveWherebyScope so the rule can
  // never drift between call surfaces. The route gate (roomUnavailableForRoute) and the call pin
  // (folded into effActivity*/activityTeamId above) are applied HERE; the helper only decides
  // dedicated-vs-team given that already-resolved context.
  const { isActivityScope, isSprintScope, teamRoomTeamId, scopeTeamId, hasScope } = resolveWherebyScope({
    sprintId: effSprintId,
    activityId: effActivityId,
    activityType: effActivityType,
    teamId: activityTeamId,
    roomUnavailableForRoute
  })

  // staleTime 0 keeps the team doc fresh for name/context; the room itself comes from the table.
  const {
    data: teamResponse,
    isLoading: isTeamLoading,
    isFetching
  } = useTeam(scopeTeamId ?? '', { enabled: Boolean(scopeTeamId), staleTime: 0 })

  const activityQuery = useActivityWherebyMeeting(
    isActivityScope ? { teamId: activityTeamId, activityId: effActivityId } : undefined
  )
  const teamQuery = useTeamWherebyMeeting(teamRoomTeamId)

  const ensureActivityMutation = useEnsureActivityWherebyMeeting()
  const createTeamMutation = useCreateWherebyMeeting()

  const activeResponse = isActivityScope ? activityQuery.data : teamQuery.data
  const isMeetingFetching = isActivityScope ? activityQuery.isFetching : teamQuery.isFetching

  const refetch = useCallback(() => {
    if (isActivityScope) void activityQuery.refetch()
    else void teamQuery.refetch()
  }, [isActivityScope, activityQuery, teamQuery])

  const ensureRoom = useCallback(() => {
    if (isActivityScope && activityTeamId && effSprintId && effActivityId) {
      ensureActivityMutation.mutate(
        { teamId: activityTeamId, sprintId: effSprintId, activityId: effActivityId },
        { onSuccess: () => refetch() }
      )
      return
    }
    if (teamRoomTeamId) {
      createTeamMutation.mutate(
        { roomNamePrefix: teamRoomTeamId, teamId: teamRoomTeamId },
        { onSuccess: () => refetch() }
      )
    }
  }, [
    isActivityScope,
    activityTeamId,
    teamRoomTeamId,
    effSprintId,
    effActivityId,
    ensureActivityMutation,
    createTeamMutation,
    refetch
  ])

  const meetingInfo = useMemo<WherebyRoomInfo | null>(() => {
    // Nothing to connect to when there's no resolved scope. The team DOCUMENT loading is irrelevant
    // here — the room URL comes from the (cloud-function) meeting query, and gating on the client-side
    // team read made the call hang on "Preparing room" on the heavy game page where that read starves.
    if (!scopeTeamId) {
      return null
    }

    const team = teamResponse?.data as Team | undefined
    const meeting = (activeResponse?.data as WherebyMeeting | null | undefined) ?? undefined
    const displayName = getWherebyCallDisplayName(user)
    // Team name for the call header. Prefer the team doc; else the name carried on the room itself
    // (stamped server-side at creation — reliable even when the client team-doc read starves). NEVER
    // fall back to the raw team id — show a generic label instead, matching the 'Team' fallback used
    // elsewhere (NextActivity / AI coach).
    const teamName = team?.name ?? meeting?.teamName ?? 'Team'

    // Note: Use roomUrl only, not hostRoomUrl. The hostRoomUrl is meant for the AI assistant
    // to join with host privileges, not for regular participants.
    return {
      team,
      meeting,
      roomUrl: meeting?.roomUrl,
      teamName,
      displayName
    }
  }, [scopeTeamId, teamResponse?.data, activeResponse?.data, user])

  const sprintCall = useMemo<SprintActivityWherebyMeetingRequest | undefined>(() => {
    const hasSprintCall = isSprintScope && effSprintId !== undefined && effActivityId !== undefined
    return hasSprintCall ? { sprintId: effSprintId, activityId: effActivityId } : undefined
  }, [isSprintScope, effSprintId, effActivityId])

  const isLoading = isSessionLoading || isUserLoading || isTeamLoading

  return {
    meetingInfo,
    isLoading,
    isFetching: isFetching || isMeetingFetching,
    error: activityQuery.error ?? teamQuery.error,
    ensureRoom,
    refetch,
    isEnsuring: ensureActivityMutation.isPending || createTeamMutation.isPending,
    roomUnavailableForRoute,
    hasScope,
    isGameScope: isActivityScope && WHEREBY_GAME_ACTIVITY_TYPES.has(effActivityType as string),
    sprintCall,
    teamId: scopeTeamId
  }
}
